import { useState } from "react"
import { api } from "../api/client.ts"
import type { ItemMaster } from "../api/types.ts"
import { useApiQuery, useAsyncAction, useDebounced } from "../hooks/index.ts"
import { useI18n } from "../i18n/index.tsx"
import { useSession } from "../auth/SessionProvider.tsx"
import { hsCodeDigits } from "../lib/hs-code.ts"
import { DEFAULT_ORIGIN, ORIGIN_COUNTRIES, isOriginCountry, originLabel } from "../lib/origins.ts"
import {
  Badge,
  Button,
  Card,
  CardHeader,
  DataTable,
  Drawer,
  EmptyState,
  ErrorNote,
  Field,
  FormGrid,
  Input,
  PageHeader,
  Select,
  SkeletonTable,
  useToast,
  type Column,
} from "../ui/index.ts"

const EMPTY = {
  code: "",
  nameKo: "",
  nameEn: "",
  hsCode: "",
  originCountry: DEFAULT_ORIGIN as string,
  defaultUnitPrice: "",
}

type FormValues = typeof EMPTY

/** 품목 하나를 폼 값으로. 없는 값은 빈 칸으로 둔다(서버는 null 로 되돌려 받는다). */
function toForm(item: ItemMaster): FormValues {
  return {
    code: item.code,
    nameKo: item.nameKo,
    nameEn: item.nameEn,
    hsCode: item.hsCode,
    // 원산지는 셋 중에서 고른다. 비어 있거나 셋 밖인 옛 값은 기본값으로 세워 둔다
    // — 고른 값만 저장되므로 판을 닫지 않고 그대로 저장하면 이 값이 된다.
    originCountry:
      item.originCountry !== null && isOriginCountry(item.originCountry)
        ? item.originCountry
        : DEFAULT_ORIGIN,
    defaultUnitPrice: item.defaultUnitPrice ?? "",
  }
}

/** 글자를 칠 때마다 서버를 부르지 않는다. 멎으면 그때 한 번. */
const SEARCH_DEBOUNCE_MS = 200

export function ItemMasterPage() {
  const { t } = useI18n()
  const { user } = useSession()
  const toast = useToast()
  const [search, setSearch] = useState("")
  const [form, setForm] = useState<FormValues>(EMPTY)
  /** 판이 열려 있으면 무엇을 하는 중인가 — 새로 넣는 중이거나, 어떤 품목을 고치는 중이다. */
  const [editing, setEditing] = useState<{ item: ItemMaster | null } | null>(null)

  const isAdmin = user?.role === "admin"
  const settledSearch = useDebounced(search, SEARCH_DEBOUNCE_MS)

  const params = new URLSearchParams()
  if (settledSearch) params.set("q", settledSearch)
  // 관리자만 꺼 둔 품목까지 본다(끄고 켜는 것도 관리자만 한다).
  if (isAdmin) params.set("includeInactive", "true")

  const query = useApiQuery<{ items: ItemMaster[] }>(`/api/item-masters?${params}`)
  const action = useAsyncAction()
  const items = query.data?.items ?? null

  function openAdd() {
    setForm(EMPTY)
    setEditing({ item: null })
  }

  function openEdit(item: ItemMaster) {
    setForm(toForm(item))
    setEditing({ item })
  }

  /**
   * 새 품목은 POST, 있는 품목은 PATCH.
   *
   * 고칠 때 코드는 보내지 않는다 — 품목을 가리키는 이름이라 바꾸면 사람들이 외우던 값이
   * 달라진다(건에 남은 인보이스 품목은 id 로 이어져 있어 데이터가 깨지지는 않는다).
   */
  async function submit() {
    if (!editing) return
    const target = editing.item
    const body = {
      nameKo: form.nameKo,
      nameEn: form.nameEn,
      hsCode: form.hsCode,
      originCountry: form.originCountry,
      defaultUnitPrice: form.defaultUnitPrice || null,
    }

    const ok = await action.run(() =>
      target
        ? api.patch(`/api/item-masters/${target.id}`, body)
        : api.post("/api/item-masters", { ...body, code: form.code }),
    )
    if (!ok) return

    setForm(EMPTY)
    setEditing(null)
    toast(t("common.saved"))
    await query.reload()
  }

  async function toggleActive(item: ItemMaster) {
    const ok = await action.run(() => api.patch(`/api/item-masters/${item.id}`, { active: !item.active }))
    if (ok) await query.reload()
  }

  const columns: Column<ItemMaster>[] = [
    { key: "code", header: t("itemMasters.code"), primary: true, render: (row) => row.code },
    { key: "nameKo", header: t("itemMasters.nameKo"), render: (row) => row.nameKo },
    { key: "nameEn", header: t("itemMasters.nameEn"), priority: "md", render: (row) => row.nameEn },
    { key: "hsCode", header: t("itemMasters.hsCode"), render: (row) => row.hsCode },
    {
      key: "originCountry",
      header: t("itemMasters.origin"),
      priority: "md",
      render: (row) => originLabel(row.originCountry, t) ?? "—",
    },
    {
      key: "price",
      header: t("itemMasters.price"),
      align: "end",
      priority: "lg",
      render: (row) => row.defaultUnitPrice ?? "—",
    },
    {
      key: "org",
      header: t("itemMasters.org"),
      priority: "lg",
      render: (row) => (row.orgId === null ? t("itemMasters.common") : row.orgId),
    },
  ]

  if (isAdmin) {
    // 흐린 글씨 대신 뱃지로 알린다 — opacity 로 죽이면 읽기 어렵고 접근성에도 나쁘다.
    columns.push({
      key: "active",
      header: t("itemMasters.active"),
      render: (row) =>
        row.active ? (
          <Button size="sm" variant="ghost" onClick={() => void toggleActive(row)}>
            {t("common.yes")}
          </Button>
        ) : (
          <button type="button" className="badge-button" onClick={() => void toggleActive(row)}>
            <Badge tone="neutral">{t("itemMasters.inactive")}</Badge>
          </button>
        ),
    })
    columns.push({
      key: "edit",
      header: "",
      render: (row) => (
        <Button size="sm" icon="edit" onClick={() => openEdit(row)}>
          {t("itemMasters.edit")}
        </Button>
      ),
    })
  }

  const error = action.error ?? query.error
  const isNew = editing?.item == null
  const canSubmit =
    !action.busy && form.nameKo && form.nameEn && form.hsCode && (!isNew || form.code !== "")

  return (
    <>
      <PageHeader
        title={t("itemMasters.title")}
        actions={
          isAdmin ? (
            <Button variant="primary" icon="plus" onClick={openAdd}>
              {t("itemMasters.add")}
            </Button>
          ) : null
        }
      />

      <ErrorNote error={error} />

      <Card>
        <Field label={t("items.search")} htmlFor="master-search">
          <Input
            id="master-search"
            icon="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </Field>
      </Card>

      <Card padded={false}>
        <CardHeader title={t("itemMasters.searchTitle")} icon="tag" />
        {!items ? (
          <div className="table-pad">
            <SkeletonTable rows={5} />
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={items}
            rowKey={(row) => row.id}
            onRowClick={isAdmin ? (row) => openEdit(row) : undefined}
            empty={<EmptyState icon="tag" title={t("itemMasters.empty")} />}
          />
        )}
      </Card>

      {editing ? (
        <Drawer
          title={isNew ? t("itemMasters.add") : t("itemMasters.editTitle")}
          onClose={() => setEditing(null)}
          footer={
            <div className="inline">
              <Button
                variant="primary"
                loading={action.busy}
                disabled={!canSubmit}
                onClick={() => void submit()}
              >
                {isNew ? t("itemMasters.add") : t("common.save")}
              </Button>
              <Button onClick={() => setEditing(null)}>{t("common.cancel")}</Button>
            </div>
          }
        >
          <ErrorNote error={error} />
          <FormGrid columns={2}>
            <Field
              label={t("itemMasters.code")}
              htmlFor="m-code"
              required={isNew}
              hint={isNew ? undefined : t("itemMasters.codeLocked")}
            >
              <Input
                id="m-code"
                value={form.code}
                // 이미 있는 품목의 코드는 고치지 않는다 — 사람들이 그 값으로 품목을 부른다.
                disabled={!isNew}
                onChange={(event) => setForm({ ...form, code: event.target.value })}
              />
            </Field>
            <Field label={t("itemMasters.hsCode")} htmlFor="m-hs" required>
              <Input
                id="m-hs"
                inputMode="numeric"
                value={form.hsCode}
                // 점을 쳐도 들어가지 않는다 — 신고 모양이 숫자만이다(lib/hs-code.ts).
                onChange={(event) => setForm({ ...form, hsCode: hsCodeDigits(event.target.value) })}
              />
            </Field>
            <Field label={t("itemMasters.nameKo")} htmlFor="m-ko" required>
              <Input
                id="m-ko"
                value={form.nameKo}
                onChange={(event) => setForm({ ...form, nameKo: event.target.value })}
              />
            </Field>
            <Field label={t("itemMasters.nameEn")} htmlFor="m-en" required>
              <Input
                id="m-en"
                value={form.nameEn}
                onChange={(event) => setForm({ ...form, nameEn: event.target.value })}
              />
            </Field>
            <Field label={t("itemMasters.origin")} htmlFor="m-origin" hint={t("itemMasters.originHint")}>
              <Select
                id="m-origin"
                value={form.originCountry}
                onChange={(event) => setForm({ ...form, originCountry: event.target.value })}
              >
                {ORIGIN_COUNTRIES.map((origin) => (
                  <option key={origin} value={origin}>
                    {t(`origin.${origin}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t("itemMasters.price")} htmlFor="m-price">
              <Input
                id="m-price"
                inputMode="decimal"
                value={form.defaultUnitPrice}
                onChange={(event) => setForm({ ...form, defaultUnitPrice: event.target.value })}
              />
            </Field>
          </FormGrid>
        </Drawer>
      ) : null}
    </>
  )
}
